<!-- eslint-disable max-len -->
<template>
  <LocalFileUploaderModal
    hideHeader
    :modalSize="'3xl'"
    :isModalVisible="isUploadAudioModalVisible"
    :staticState="!isUploadingAudioDone && !isUploadingAudio"
    :uploadingState="isUploadingAudio"
    :uploadError="uploadAudioError"
    :featureName="UploadFeatureName.UPLOAD_AUDIO"
    :instructionText="instructionText"
    :fileFormat="'.mp3'"
    :dropPlaceholder="'drop audio'"
    :icon="audioIcon"
    :selectedTab="tab"
    @hide="hide()"
  >
    <template #headerSection>
      <div class="relative">
        <div class="modal__title--tab">
          <a :class="{active: tab == UPLOAD}" @click="selectTab(UPLOAD)">{{ $i18nPresenter.t(UPLOAD) }}</a>
          <a :class="{active: tab == EXISTING_TRACKS}" @click="selectTab(EXISTING_TRACKS)">{{ $i18nPresenter.t(EXISTING_TRACKS) }}</a>
        </div>
        <aha-icon
          name="close"
          height="20"
          width="20"
          viewBox="0 0 24 24"
          @click="hide()"
          class="cursor-pointer text-base-70 hover:text-primary-90 absolute -top-3 -right-3"
        />
      </div>
    </template>
    <template #secondTabContentSection>
      <div class="upload-audio__existing-files" id="infinite-list">
        <p v-if="!audios.length">
          {{ $i18nPresenter.t('Audio tracks that have been uploaded to this presentation will be listed here.') }}
        </p>
        <table class="existing-files__table" v-if="audios.length">
          <thead>
            <tr class="existing-files__table-header">
              <th class="existing-files__table-header-text">
                <strong>{{ $i18nPresenter.t('Slide') }}</strong>
              </th>
              <th class="existing-files__table-header-text">
                <strong>{{ $i18nPresenter.t('Name') }}</strong>
              </th>
              <th class="border-top-0 d-none d-sm-table-cell" />
            </tr>
          </thead>

          <tbody>
            <tr v-for="(item, index) in audios" :key="index">
              <td class="existing-files__table-content">
                Slide {{ item.order }}
              </td>
              <td>
                <div class="cursor-pointer hover:text-primary-90" @click="selectAudio(item, $event)">
                  {{ item.name }}
                </div>
              </td>
              <td class="d-none d-sm-table-cell">
                <audio :src="item.link.url" controls class="existing-files__table-audio" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </LocalFileUploaderModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Socket from '@/socket'
import SlideApi from '@/api/slide'
import Constant from '@/constant'
import Util from '@/util'
import UploadFeatureName from '@/constant/uploadFeatureName'
import EventBus from '@/eventBus'
import ActiveTabNames from '@/constant/ActiveTabNames'

// components
import LocalFileUploaderModal from '@/components-v2/modals/LocalFileUploaderModal.vue'

import eventCategories from '@/constant/eventCategories'

const { PRESENTATION_EDITOR } = eventCategories

export default {
  components: {
    LocalFileUploaderModal,
  },
  data() {
    return {
      tab: ActiveTabNames.UPLOAD,
      text: '',
      file: null,
      isLoadingImg: false,
      audios: [],
      UploadFeatureName,
      UPLOAD: ActiveTabNames.UPLOAD,
      EXISTING_TRACKS: ActiveTabNames.EXISTING_TRACKS,
    }
  },
  computed: {
    ...mapGetters('audioUploader', ['isUploadAudioModalVisible', 'isUploadingAudioDone', 'uploadAudioError', 'isUploadingAudio']),

    audioIcon() {
      return {
        name: 'music-note',
        width: '68',
        height: '68',
        viewBox: '0 0 24 24',
      }
    },
    instructionText() {
      return this.$i18nPresenter.t('MP3 - Max: 15MB')
    },
  },
  mounted() {
    this.setShowUploadAudioModalAction(true)
    this.setIsUploadingAudioDoneAction(false)
    this.setUploadAudioErrorAction(null)
    this._getVideoData()

    EventBus.$on('Upload Audio: updateSlides', (payload) => {
      this.updateSlides(payload)
    })

    setTimeout(() => {
      // Disable deselect
      const modal = document.getElementById('upload-image-modal')
      if (modal) {
        modal.addEventListener('mouseup', (e) => {
          e.stopPropagation()
        }, true)
      }
    }, 0)
  },
  destroyed() {
    EventBus.$off('Upload Audio: updateSlides')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    ...mapActions('audioUploader', ['setShowUploadAudioModalAction', 'setIsUploadingAudioDoneAction', 'setUploadAudioErrorAction']),

    _getVideoData() {
      const arr = []
      this.$store.state.presentation.slides.forEach((item, index) => {
        if (!item.audioLink) return

        arr.push({
          name: item.audioName,
          link: item.audioLink,
          order: index + 1,
        })
      })

      this.audios = arr
    },
    onEsc() {
      this.hide()
    },
    async selectAudio(item, event) {
      event.preventDefault()
      const payload = {}
      this.updateSlideActiveAction({
        audioName: item.name,
        audioLink: {
          name: item.link.name,
          url: item.link.url,
        },
      })
      this.hide()
      payload.presentationId = this.$store.state.presentation.id
      payload.accessCode = this.$store.state.presentation.uniqueAccessCode
      payload.socketId = Socket.getSocketId()
      payload.presenting = this.$store.getters.isPresenting || this.$store.getters.isBackstage
      payload.audioLink = item.link.url
      payload.audioName = item.name

      await this.updateSlides(payload)

      Util.mixpanelTracking(
        PRESENTATION_EDITOR,
        this.$store.getters.selectedSlides.length > 1
          ? 'Audio Tab > Select audio for multiple slides'
          : 'Audio Tab > Select audio for single slide',
        `Presentation: ${this.$store.state.presentation.id}`,
      )
    },

    async updateSlides(payload) {
      const instantPayload = {}
      Constant.commonSlideAttributes.forEach((attr) => {
        if (payload[attr] !== undefined) {
          instantPayload[attr] = payload[attr]
          // FIXME: Get & Set data should be the same format so we don't have to do this
          if (attr === 'audioLink') {
            instantPayload[attr] = {
              url: instantPayload[attr],
              name: payload.audioName,
            }
          }
        }
      })

      const appliedSlides = this.$store.getters.selectedSlides
      this.$store.dispatch('updateSlideActive', instantPayload)
      const slides = appliedSlides.map((slide) => ({
        id: slide.id,
        ...payload,
      }))

      try {
        this.$store.dispatch('setSyncPresentation', 1)
        await SlideApi.updateSlides({
          socketId: Socket.getSocketId(),
          presentationId: this.$store.state.presentation.id,
          slides,
        })

        appliedSlides.forEach((slide) => {
          Constant.commonSlideAttributes.forEach((attr) => {
            if (instantPayload[attr] !== undefined) {
              slide[attr] = instantPayload[attr]
            }
          })
        })

        this.$store.dispatch('setSyncPresentation', 2)
      } catch (err) {
        this.$store.dispatch('setSyncPresentation', 3)
        throw err
      }
    },
    hide() {
      this.setShowUploadAudioModalAction(false)
    },
    selectTab(name) {
      this.tab = name
    },
  },
}
</script>
